import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';

import { routeTree } from './route-tree';
import { AuthProvider } from './auth/auth-provider';
import { ThemeProvider } from './theme/theme-provider';
import { ImpersonationBanner } from './components/layout/impersonation-banner';
import '@fontsource/exo-2/400.css';
import '@fontsource/exo-2/500.css';
import '@fontsource/exo-2/600.css';
import '@fontsource/exo-2/700.css';
import '@fontsource/exo-2/800.css';
import './styles/global.css';

/**
 * Frontend entrypoint — wires the providers in the order the app expects:
 *
 *   Sentry → QueryClient → AuthProvider → Router
 *
 * AuthProvider depends on QueryClient (it owns the `/me` query), and the
 * Router consumes auth state via the `useAuth` hook in route loaders.
 *
 * Sentry error monitoring (#72222): init when VITE_SENTRY_DSN is set.
 * Without a DSN the SDK is not loaded — zero overhead in dev/test.
 */

if (
  typeof import.meta.env.VITE_SENTRY_DSN === 'string' &&
  import.meta.env.VITE_SENTRY_DSN.length > 0
) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    beforeSend(event) {
      if (event.request?.data) {
        event.request.data = '[redacted]';
      }
      return event;
    },
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry auth failures — let the API interceptor handle the refresh
        // flow and re-throw on terminal failure.
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    // Filled in by the provider stack at render time.
    auth: undefined as never,
    queryClient,
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  return (
    <StrictMode>
      <Sentry.ErrorBoundary
        fallback={
          <p className="p-8 text-center text-error">Aplikace spadla. Zkuste obnovit stránku.</p>
        }
      >
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              {(auth) => (
                <>
                  <ImpersonationBanner />
                  <RouterProvider router={router} context={{ auth, queryClient }} />
                </>
              )}
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}
createRoot(rootElement).render(<App />);
